import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  CircleAlert, CircleCheck, File, FileArchive, FileCode, FileImage, FileText, Globe, LoaderCircle, Mail, RefreshCw, Send,
} from 'lucide-react';
import './ManualConversation.css';

// Carrier requirements used to display the extra fields in the manual form,
// keyed by the canonical automation form (see getAutomationFormKey).
const carrierRequirements = {
  "Mondial Relay": ["tracking_number", "option_text", "shop_name", "message"],
  "Colis Privé": ["tracking_number", "package_value", "motif", "comment"],
  "Chronopost": {
    always: ["tracking_number"],
    conditional: {
      objet: {
        "Instruction de livraison": ["motif", "comment"],
        "Contestation de livraison": ["motif", "comment"],
        "Demande de preuve de livraison": ["motif", "comment"],
        "SWAP": ["motif", "comment"],
        "Signaler un dysfonctionnement": ["motif", "comment"],
        "Fraude": ["package_content", "product_family", "comment"],
        "Livraison partielle ou endommagée": ["package_content", "product_family", "comment"],
        "Position Colis": ["package_content", "product_family", "comment"],
        "Retour Expéditeur": ["package_content", "product_family", "comment"],
        "Récupérer colis en Pack City": ["package_content", "product_family", "comment"],
        "Retard / Hors délais": ["comment"],
        "Retour express de paiement": ["package_content", "comment"]
      }
    }
  },
  "Colissimo": ["tracking_number", "motif_text", "comments", "colis_content", "colis_value"],
  "Amazon": ["tracking_id", "price"],
  "DHL": ["tracking_id", "comment"]
};

// Maps any configured carrier name to the automation form it uses, mirroring the
// carrier normalisation done by the mail microservice. Carriers without a match
// (or flagged is_mail) are contacted by e-mail instead.
const AUTOMATION_FORM_MATCHERS = [
  { match: /mondial\s*relay/i, key: 'Mondial Relay' },
  { match: /colis[\s-]*priv/i, key: 'Colis Privé' },
  { match: /chrono/i, key: 'Chronopost' },
  { match: /colissimo/i, key: 'Colissimo' },
  { match: /amazon/i, key: 'Amazon' },
  { match: /dhl/i, key: 'DHL' }
];

const getAutomationFormKey = (carrierName) => {
  const matcher = AUTOMATION_FORM_MATCHERS.find(({ match }) => match.test(carrierName || ''));
  return matcher ? matcher.key : null;
};

const chronopostRequirements = {
  "Instruction de livraison": {
    motif_required: true,
    motif_options: [
      "Reprogrammer une date de livraison",
      "Mise à disposition dans un point de retrait",
      "Changement d'adresse"
    ],
    additional_fields: ["comment"]
  },
  "Contestation de livraison": {
    motif_required: true,
    motif_options: [
      "En main propre",
      "En boite aux lettres",
      "En Pack City"
    ],
    additional_fields: ["comment"]
  },
  "Demande de preuve de livraison": {
    motif_required: true,
    motif_options: [
      "Demander une confirmation de livraison",
      "Obtenir la preuve de livraison"
    ],
    additional_fields: ["comment"]
  },
  "SWAP": {
    motif_required: true,
    motif_options: [
      "Non récupéré",
      "Annulation",
      "Récupéré partiellement"
    ],
    additional_fields: ["comment"]
  },
  "Signaler un dysfonctionnement": {
    motif_required: true,
    motif_options: [
      "Heure de livraison erronée / Pas d'avis de passage",
      "Comportement Chauffeur",
      "Problème point relais",
      "Non respect des consignes",
      "Problème température",
      "Demande courbe de température"
    ],
    additional_fields: ["comment"]
  },
  "Fraude": {
    motif_required: false,
    motif_options: [],
    additional_fields: ["package_content", "product_family", "comment"]
  },
  "Livraison partielle ou endommagée": {
    motif_required: false,
    motif_options: [],
    additional_fields: ["package_content", "product_family", "comment"]
  },
  "Position Colis": {
    motif_required: false,
    motif_options: [],
    additional_fields: ["package_content", "product_family", "comment"]
  },
  "Retour Expéditeur": {
    motif_required: false,
    motif_options: [],
    additional_fields: ["package_content", "product_family", "comment"]
  },
  "Récupérer colis en Pack City": {
    motif_required: false,
    motif_options: [],
    additional_fields: ["package_content", "product_family", "comment"]
  },
  "Retard / Hors délais": {
    motif_required: false,
    motif_options: [],
    additional_fields: ["comment"]
  },
  "Retour express de paiement": {
    motif_required: false,
    motif_options: [],
    additional_fields: ["package_content", "comment"]
  }
};

const productFamilyOptions = [
  "Autre",
  "Informatique / Electronique",
  "Jouet",
  "Textile",
  "Téléphone",
  "Aliment",
  "Document",
  "Médicaments / Matériel Médical",
  "Pièce Auto",
  "Bagagerie"
];

const colisPriveMotifOptions = [
  "Colis non livré / colis retardé",
  "Colis endommagé",
  "Comportement livreur",
  "Colis annoncé livré et non réceptionné",
  "Problématique relais",
  "Colis retourné a l'expéditeur"
];

const colissimoMotifOptions = [
  "Contestation motif de retour",
  "Colis endommagé",
  "Colis non livré",
  "Contestation de livraison",
  "Consignes de livraison",
  "Preuve de livraison"
];

// Mondial Relay shop labels come from the carrier's synced enseignes (see the
// carrier page): a hardcoded list belonged to one tenant only.


const mondialRelayOptions = [
  "Litiges / Livraison non conforme",
  "Instructions expéditeur",
  "Déclencher une enquête / Où est mon colis ?",
  "Demande de preuve de livraison"
];

const ManualConversation = ({ onRefresh, ticketFiles, ticket }) => {
  const { t } = useTranslation();
  const [carriers, setCarriers] = useState([]);
  // 'loading' | 'ready' | 'error': the list is the tenant's configured carriers
  // only — never a hardcoded fallback (KI-196: an employee whose list failed
  // to load was shown carriers its tenant does not have).
  const [carriersStatus, setCarriersStatus] = useState('loading');
  const [descriptors, setDescriptors] = useState([]);
  const [manualCarrier, setManualCarrier] = useState("");
  const [manualParams, setManualParams] = useState({});
  const [selectedObjet, setSelectedObjet] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const { ticketId: routeTicketId } = useParams();
  const ticketId = ticket?._id || routeTicketId;

  // Load the carriers configured for the account (an employee gets its
  // tenant's) so any of them can be selected.
  const fetchCarriers = async () => {
    setCarriersStatus('loading');
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/logistics/carriers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarriers(Array.isArray(response.data) ? response.data : []);
      setCarriersStatus('ready');
    } catch (err) {
      console.error('Error fetching carriers list:', err);
      setCarriers([]);
      setCarriersStatus('error');
    }
  };

  useEffect(() => {
    // Carrier descriptor registry (backend plan C1): canonical names, channels
    // and motif vocabularies; the hardcoded tables above stay as the fallback.
    const fetchDescriptors = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get('/api/logistics/carrier-descriptors', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDescriptors(response.data?.descriptors || []);
      } catch (err) {
        setDescriptors([]);
      }
    };
    fetchCarriers();
    fetchDescriptors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const COMPONENT_KEYS = { 'Amazon Shipping': 'Amazon' };
  const descriptorFor = (name) => {
    if (!name || !descriptors.length) return null;
    const key = String(name).toLowerCase().trim();
    return descriptors.find((d) => (d.identity?.aliases || []).some((alias) => key.includes(alias))) || null;
  };
  // The automation form of a configured carrier: from the registry (a carrier
  // with a portal channel) when loaded, else the legacy regex matchers.
  const automationKeyFor = (name) => {
    const d = descriptorFor(name);
    if (d) {
      const automated = (d.channels || []).some((c) => c.type === 'portal');
      return automated ? (COMPONENT_KEYS[d.name] || d.name) : null;
    }
    return getAutomationFormKey(name);
  };
  const motifOptionsFor = (componentKey, field, fallback) => {
    const d = descriptors.find((x) => (COMPONENT_KEYS[x.name] || x.name) === componentKey);
    const options = d?.motifs?.[field];
    return Array.isArray(options) && options.length ? options : fallback;
  };

  const selectedCarrier = carriers.find(c => c.name === manualCarrier) || null;
  // A carrier flagged is_mail is always contacted by e-mail; otherwise carriers
  // matching a known automation form use web automation.
  const automationFormKey = selectedCarrier && selectedCarrier.is_mail === true
    ? null
    : automationKeyFor(manualCarrier);
  const isMailCarrier = !!manualCarrier && !automationFormKey;
  const mondialRelayShops = Array.isArray(selectedCarrier?.enseignes) ? selectedCarrier.enseignes : [];

  const handleParamChange = (field, value) => {
    setManualParams(prevParams => {
      const newParams = {
        ...prevParams,
        [field]: value
      };
      // Sync tracking_number and tracking_id
      if (field === 'tracking_number') {
        newParams.tracking_id = value;
      } else if (field === 'tracking_id') {
        newParams.tracking_number = value;
      }
      return newParams;
    });
  };

  // Pre-fill params when carrier changes using ticket data
  const handleCarrierChange = (e) => {
    const carrier = e.target.value;
    setManualCarrier(carrier);

    const formKey = automationKeyFor(carrier);

    // Initialize params with ticket data when available
    const initialParams = {};
    if (ticket) {
      if (formKey === "Colis Privé" || formKey === "Colissimo") {
        if (ticket.package_value) initialParams.package_value = ticket.package_value;
        if (ticket.package_content) initialParams.package_content = ticket.package_content;
      }
      if (ticket.tracking_number) {
        initialParams.tracking_number = ticket.tracking_number;
        initialParams.tracking_id = ticket.tracking_number; // Sync tracking_id with tracking_number
      }
    }
    
    setManualParams(initialParams);
    setSelectedObjet("");
  };

  // Fields the carrier form requires for the current selection (objet-specific
  // for Chronopost). Enforced before the request leaves: 2026-09-08 a
  // 'Position Colis' request went out without a product family and was refused.
  const missingRequiredFields = () => {
    const requirements = carrierRequirements[automationFormKey];
    if (!requirements) return [];
    let fields = [];
    if (Array.isArray(requirements)) {
      fields = requirements;
    } else {
      fields = [...(requirements.always || [])];
      if (!selectedObjet) {
        fields.push('objet');
      } else {
        const objetFields = (requirements.conditional?.objet?.[selectedObjet] || []).filter(
          field => field !== 'motif' || chronopostRequirements[selectedObjet]?.motif_required
        );
        fields = fields.concat(objetFields);
      }
    }
    return fields.filter(field => !String(manualParams[field] ?? '').trim());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isMailCarrier && !(manualParams.message || "").trim()) {
      setError(t('manualConversation.messageRequired') || "A message is required to contact this carrier.");
      return;
    }
    const missing = missingRequiredFields();
    if (missing.length > 0) {
      setError(t('manualConversation.missingRequired', 'Please fill in: {{fields}}', {
        fields: missing.map(field => t(`manualConversation.${field}`) || field).join(', ')
      }));
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSuccessMessage("");

    const token = localStorage.getItem('access_token');
    const formData = new FormData();
    formData.append("carrier_name", manualCarrier);
    formData.append("ticket_id", ticketId);
    formData.append("params", JSON.stringify(manualParams));
    
    if (ticketFiles?.length > 0 && automationFormKey !== "Mondial Relay") {
      formData.append("file_urls", JSON.stringify(ticketFiles.map(file => ({
        url: file.url,
        file_type: file.mime_type,
        file_name: file.name
      }))));
    }

    try {
      const response = await axios.post(
        '/api/webhook/manual-carrier-conversation',
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      // The request is sent when it is filed (backend send_operator_request):
      // say what really happened — sent, filed by the platform, or not sent and why.
      const delivery = response?.data?.delivery;
      if (delivery === 'held') {
        setError(t('manualConversation.notSent', 'Request saved but NOT sent to the carrier: {{reason}}',
          { reason: response?.data?.detail || '' }));
      } else {
        setSuccessMessage(delivery === 'queued'
          ? t('manualConversation.queued', 'Request recorded: the platform is filing it with the carrier.')
          : delivery === 'sent'
            ? t('manualConversation.sent', 'Request sent to the carrier.')
            : t('manualConversation.success'));
      }
      setManualCarrier("");
      setManualParams({});
      setSelectedObjet("");
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error starting manual carrier conversation:', err);
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' && detail ? detail : t('manualConversation.error'));
    }
    setIsSubmitting(false);
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.includes('pdf')) {
      return <FileText strokeWidth={1.8} aria-hidden="true" />;
    } else if (mimeType.includes('image')) {
      return <FileImage strokeWidth={1.8} aria-hidden="true" />;
    } else if (mimeType.includes('zip') || mimeType.includes('rar')) {
      return <FileArchive strokeWidth={1.8} aria-hidden="true" />;
    } else if (mimeType.includes('code') || mimeType.includes('json')) {
      return <FileCode strokeWidth={1.8} aria-hidden="true" />;
    }
    return <File strokeWidth={1.8} aria-hidden="true" />;
  };

  const getFileClass = (mimeType) => {
    if (mimeType.includes('pdf')) {
      return 'pdf';
    } else if (mimeType.includes('image')) {
      return 'image';
    }
    return 'document';
  };

  // One labelled field: label above, control, optional help under it.
  const fieldId = (field) => `manual-${field.replace(/[^a-z0-9_-]/gi, '-')}`;
  const renderField = (field, label, control, help) => (
    <div key={field} className="u-field param-item">
      <label className="u-label param-label" htmlFor={fieldId(field)}>{label}</label>
      {control}
      {help && <p className="u-help manual-form-help">{help}</p>}
    </div>
  );

  const textInput = (field, extra = {}) => (
    <input
      id={fieldId(field)}
      type="text"
      value={manualParams[field] || ""}
      onChange={(e) => handleParamChange(field, e.target.value)}
      className="u-input param-input"
      {...extra}
    />
  );

  const selectInput = (field, options, translate = true) => (
    <select
      id={fieldId(field)}
      value={manualParams[field] || ""}
      onChange={(e) => handleParamChange(field, e.target.value)}
      className="u-select param-input"
    >
      <option value="">
        {t('manualConversation.selectOption') || "Select an option"}
      </option>
      {options.map(option => (
        <option key={option} value={option}>
          {translate ? (t(`manualConversation.${option}`) || option) : option}
        </option>
      ))}
    </select>
  );

  const renderAdditionalFields = () => {
    if (!manualCarrier) return null;

    // E-mail carriers only need the message to send.
    if (isMailCarrier) {
      return (
        <>
          {renderField('tracking_number', t('manualConversation.tracking_number') || "tracking_number", textInput('tracking_number'))}
          {renderField('message', t('manualConversation.message') || "message", (
            <textarea
              id={fieldId('message')}
              value={manualParams.message || ""}
              onChange={(e) => handleParamChange('message', e.target.value)}
              className="u-textarea param-input"
              rows={5}
            />
          ))}
        </>
      );
    }

    const requirements = carrierRequirements[automationFormKey];

    if (Array.isArray(requirements)) {
      return requirements.map(field => {
        const label = t(`manualConversation.${field}`) || field;
        if ((automationFormKey === "Colis Privé" && field === "motif") ||
          (automationFormKey === "Colissimo" && field === "motif_text")) {
          return renderField(field, label, selectInput(field, automationFormKey === "Colis Privé"
            ? motifOptionsFor('Colis Privé', 'motif', colisPriveMotifOptions)
            : motifOptionsFor('Colissimo', 'motif_text', colissimoMotifOptions)));
        }
        if (automationFormKey === "Mondial Relay" && field === "option_text") {
          return renderField(field, label, selectInput(field, motifOptionsFor('Mondial Relay', 'option_text', mondialRelayOptions)));
        }
        if (automationFormKey === "Mondial Relay" && field === "shop_name") {
          return mondialRelayShops.length > 0
            ? renderField(field, label, selectInput(field, mondialRelayShops, false))
            : renderField(field, label,
              textInput(field, { placeholder: t('manualConversation.shopNoEnseignesPlaceholder', 'Enseigne Connect exacte') }),
              t('manualConversation.shopNoEnseignes', 'Aucune enseigne synchronisée : synchronisez les enseignes sur la page du transporteur Mondial Relay pour les choisir dans une liste.'));
        }
        return renderField(field, label, textInput(field));
      });
    } else if (typeof requirements === 'object') {
      const alwaysFields = requirements.always;
      const objetOptions = Object.keys(requirements.conditional.objet);

      return (
        <>
          {alwaysFields.map(field => renderField(field, t(`manualConversation.${field}`) || field, textInput(field)))}
          {renderField('objet', t('manualConversation.objet') || "Select Object Type", (
            <select
              id={fieldId('objet')}
              value={selectedObjet}
              onChange={(e) => {
                setSelectedObjet(e.target.value);
                handleParamChange('objet', e.target.value);
              }}
              className="u-select param-input"
            >
              <option value="">
                {t('manualConversation.selectOption') || "Select an option"}
              </option>
              {objetOptions.map(option => (
                <option key={option} value={option}>
                  {t(`manualConversation.${option}`) || option}
                </option>
              ))}
            </select>
          ))}
          {selectedObjet && requirements.conditional.objet[selectedObjet].map(field => {
            const label = t(`manualConversation.${field}`) || field;
            if (field === "motif" && chronopostRequirements[selectedObjet]?.motif_required) {
              return renderField(field, label, selectInput(field, chronopostRequirements[selectedObjet].motif_options));
            }
            if (field === "product_family") {
              return renderField(field, label, selectInput(field, motifOptionsFor('Chronopost', 'product_family', productFamilyOptions)));
            }
            return renderField(field, label, textInput(field));
          })}
        </>
      );
    }
    return null;
  };

  return (
    <div className="manual-carrier-conversation-form tkm" data-tour="carrier-form">
      <div className="tkm-head">
        <h3 className="tkm-head__title">{t('manualConversation.title', 'Contact transporteur')}</h3>
        <p className="tkm-head__desc">
          {t(
            'manualConversation.description',
            'Ouvrez manuellement une réclamation auprès du transporteur pour ce ticket.'
          )}
        </p>
      </div>
      <form className="u-form tkm-form" onSubmit={handleSubmit}>
        <div className="u-field">
          <label className="u-label" htmlFor="manual-carrier">
            {t('manualConversation.carrierName') || "Carrier Name"}
          </label>
          <select
            id="manual-carrier"
            value={manualCarrier}
            onChange={handleCarrierChange}
            className="u-select"
            disabled={carriersStatus !== 'ready' || carriers.length === 0}
          >
            <option value="">{t('manualConversation.selectCarrier') || "Select Carrier"}</option>
            {carriers.map(carrier => (
              <option key={carrier.id || carrier.name} value={carrier.name}>
                {carrier.name}
              </option>
            ))}
          </select>
          {carriersStatus === 'error' && (
            <div className="u-banner u-banner--danger manual-carriers-error" role="alert">
              <CircleAlert strokeWidth={2} aria-hidden="true" />
              <div className="u-banner__body">
                {t(
                  'manualConversation.carriersLoadError',
                  "Impossible de charger les transporteurs de votre compte. Réessayez, ou contactez votre administrateur si le problème persiste."
                )}
              </div>
              <div className="u-banner__actions">
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={fetchCarriers}>
                  <RefreshCw strokeWidth={1.8} aria-hidden="true" />
                  {t('manualConversation.carriersRetry', 'Réessayer')}
                </button>
              </div>
            </div>
          )}
          {carriersStatus === 'ready' && carriers.length === 0 && (
            <p className="u-help">
              {t(
                'manualConversation.noCarriersConfigured',
                "Aucun transporteur n'est configuré pour ce compte : ajoutez-en dans la page Transporteurs pour le contacter d'ici."
              )}
            </p>
          )}
        </div>

        {manualCarrier && (
          <div className={`tkm-hint ${isMailCarrier ? 'mail' : 'automation'}`}>
            {isMailCarrier ? <Mail strokeWidth={1.8} aria-hidden="true" /> : <Globe strokeWidth={1.8} aria-hidden="true" />}
            <span>
              {isMailCarrier
                ? t(
                    'manualConversation.mailHint',
                    'Ce transporteur est contacté par e-mail : rédigez le message à envoyer.'
                  )
                : t(
                    'manualConversation.automationHint',
                    'La demande sera déposée automatiquement sur le portail {{carrier}}.',
                    { carrier: automationFormKey }
                  )}
            </span>
          </div>
        )}

        {renderAdditionalFields()}

        {manualCarrier && automationFormKey !== 'Mondial Relay' && ticketFiles?.length > 0 && (
          <div className="tkm-files">
            <span className="u-label">
              {t('manualConversation.filesAutoAttached', 'Pièces jointes du ticket transmises')}
            </span>
            <div className="tkm-files__list">
              {ticketFiles.map((file, index) => (
                <span key={index} className={`tkm-file ${getFileClass(file.mime_type || '')}`}>
                  {getFileIcon(file.mime_type || '')}
                  <span className="tkm-file__name">{file.name}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="u-banner u-banner--danger" role="alert">
            <CircleAlert strokeWidth={2} aria-hidden="true" />
            <div className="u-banner__body">{error}</div>
          </div>
        )}
        {successMessage && (
          <div className="u-banner u-banner--success" role="status">
            <CircleCheck strokeWidth={2} aria-hidden="true" />
            <div className="u-banner__body">{successMessage}</div>
          </div>
        )}

        <div className="tkm-actions">
          <button
            type="submit"
            disabled={isSubmitting || !manualCarrier}
            className="u-btn u-btn--primary"
            data-tour="carrier-submit"
          >
            {isSubmitting
              ? <LoaderCircle className="tkm-spin" strokeWidth={2} aria-hidden="true" />
              : <Send strokeWidth={1.8} aria-hidden="true" />}
            {isSubmitting
              ? (t('manualConversation.submitting') || "Submitting...")
              : (t('manualConversation.submit') || "Submit")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManualConversation;
